import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import DocumentUploadModal from '../../../components/ui/DocumentUploadModal';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import AGBModal from './AGBModal';


import { supabase } from '../../../lib/supabase';
import { sendRegistrationConfirmation } from '../../../services/emailService';
import {
  validateField,
  registrationValidationSchema,
  sanitizeInput,
  sanitizeDealershipName,
  sanitizeAddress,
  formatPhoneNumber
} from '../../../utils/validation';

const LegalModal = ({ title, content, onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h3 className="text-xl font-heading font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Schließen"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="prose prose-sm max-w-none text-foreground whitespace-pre-line">
            {content}
          </div>
        </div>
        
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} iconName="X">
            Abbrechen
          </Button>
          <Button variant="default" onClick={onConfirm} iconName="Check" iconPosition="left">
            Bestätigen
          </Button>
        </div>
      </div>
    </div>
  );
};

const DuplicateLicensePlateModal = ({ licensePlate, existingApplications, onCancel, onProceed }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-warning/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
            </div>
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                Kennzeichen bereits registriert
              </h3>
              <p className="text-sm text-muted-foreground">
                Das Kennzeichen <span className="font-bold text-foreground">{licensePlate}</span> wurde bereits {existingApplications?.length === 1 ? 'einmal' : `${existingApplications?.length}-mal`} registriert.
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground mb-2">
                  Wichtiger Hinweis:
                </p>
                <p className="text-sm text-muted-foreground">
                  Ein Kennzeichen kann nur einmal pro Jahr für die THG-Quote angemeldet werden. Mehrfachregistrierungen führen zur Ablehnung aller Anträge.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-foreground">
              Bestehende Registrierungen:
            </p>
            {existingApplications?.map((app, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Kunde:</p>
                    <p className="font-semibold text-foreground">{app?.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">E-Mail:</p>
                    <p className="font-semibold text-foreground">{app?.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status:</p>
                    <p className="font-semibold text-foreground">{app?.application_status}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Datum:</p>
                    <p className="font-semibold text-foreground">
                      {new Date(app?.application_date)?.toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-error mb-2">
                  Bitte überprüfen Sie:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Haben Sie dieses Fahrzeug bereits registriert?</li>
                  <li>Ist das Kennzeichen korrekt eingegeben?</li>
                  <li>Handelt es sich um ein anderes Fahrzeug mit gleichem Kennzeichen?</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onCancel} iconName="X">
            Abbrechen und Kennzeichen prüfen
          </Button>
          <Button variant="default" onClick={onProceed} iconName="AlertTriangle" iconPosition="left">
            Trotzdem fortfahren
          </Button>
        </div>
      </div>
    </div>
  );
};

const RegistrationForm = ({ onSubmit, dealershipId, onDocumentUpload, dealership }) => {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    gender: '',
    companyName: '',
    street: '',
    postalCode: '',
    city: '',
    birthdate: '',
    email: '',
    phone: '',
    licensePlate: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [serviceVoucher, setServiceVoucher] = useState(null);
  const [wantsAnnualRenewal, setWantsAnnualRenewal] = useState(null);
  const [agreements, setAgreements] = useState({
    thgNotice: false,
    authorization: false,
    privacy: false,
    terms: false,
    voucherUnderstanding: false
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showThgModal, setShowThgModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRegisterAnotherButton, setShowRegisterAnotherButton] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateApplications, setDuplicateApplications] = useState([]);
  const [proceedWithDuplicate, setProceedWithDuplicate] = useState(false);

  // Restore form data from localStorage if available (after error recovery)
  useEffect(() => {
    const restoreFormData = () => {
      try {
        const backup = localStorage.getItem('thg_form_backup');
        if (backup) {
          const parsed = JSON.parse(backup);
          // Only restore if backup is less than 1 hour old
          if (Date.now() - parsed?.timestamp < 3600000) {
            setFormData(parsed?.formData || {});
            if (parsed?.uploadedDocument) {
              // Show notification that data was restored
              console.log('Form data restored from backup');
            }
          } else {
            // Remove old backup
            localStorage.removeItem('thg_form_backup');
          }
        }
      } catch (error) {
        console.error('Error restoring form data:', error);
        localStorage.removeItem('thg_form_backup');
      }
    };

    restoreFormData();
  }, []);

  const voucherValue = dealership?.voucherValue || 150;
  const voucherCurrency = dealership?.voucherCurrency || 'Euro';
  const voucherDisplay = `${voucherValue} ${voucherCurrency}`;

  const premium2026 = Number(dealership?.premium2026 ?? dealership?.voucherValue ?? 150);
  const premium2027 = Number(dealership?.premium2027 ?? 0);
  const totalPremium = premium2026 + premium2027;
  const has2027 = premium2027 > 0;

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    
    // Choose appropriate sanitization based on field type
    let sanitized;
    if (name === 'street') {
      // Preserve spaces for addresses
      sanitized = sanitizeAddress(value);
    } else if (name === 'firstname' || name === 'lastname' || name === 'companyName') {
      // Preserve spaces for names (e.g., "Max Mustermann", "Autohaus GmbH")
      sanitized = sanitizeDealershipName(value);
    } else {
      // Standard sanitization for other fields
      sanitized = sanitizeInput(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: sanitized }));
    
    // Real-time validation
    if (touched?.[name]) {
      // Skip birthdate validation if company is selected
      if (name === 'birthdate' && formData?.gender === 'company') {
        return;
      }
      
      const rules = registrationValidationSchema?.[name];
      if (rules) {
        const error = validateField(name, sanitized, rules);
        setErrors(prev => ({ 
          ...prev, 
          [name]: error 
        }));
      }
    }
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    // Skip birthdate validation if company is selected
    if (fieldName === 'birthdate' && formData?.gender === 'company') {
      return;
    }
    
    // Validate on blur
    const rules = registrationValidationSchema?.[fieldName];
    if (rules) {
      const error = validateField(fieldName, formData?.[fieldName], rules);
      setErrors(prev => ({ 
        ...prev, 
        [fieldName]: error 
      }));
    }
    
    // Format phone number on blur
    if (fieldName === 'phone' && formData?.phone) {
      const formatted = formatPhoneNumber(formData?.phone);
      setFormData(prev => ({ ...prev, phone: formatted }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validate form fields
    Object.keys(registrationValidationSchema)?.forEach(fieldName => {
      // Skip birthdate validation if company is selected
      if (fieldName === 'birthdate' && formData?.gender === 'company') {
        return;
      }
      
      const rules = registrationValidationSchema?.[fieldName];
      const error = validateField(fieldName, formData?.[fieldName], rules);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });
    
    // Validate gender selection
    if (!formData?.gender) {
      newErrors.gender = 'Bitte wählen Sie Ihren Geschlecht';
      isValid = false;
    }

    // Validate company name if company is selected
    if (formData?.gender === 'company') {
      if (!formData?.companyName || formData?.companyName?.trim() === '') {
        newErrors.companyName = 'Bitte geben Sie den Firmennamen ein';
        isValid = false;
      }
    }

    // Validate license plate (now required)
    if (!formData?.licensePlate || !formData?.licensePlate?.trim()) {
      newErrors.licensePlate = 'Kennzeichen ist erforderlich';
      isValid = false;
    } else {
      const licensePlateError = validateField('licensePlate', formData?.licensePlate, registrationValidationSchema?.licensePlate);
      if (licensePlateError) {
        newErrors.licensePlate = licensePlateError;
        isValid = false;
      }
    }

    // Validate vehicle document upload (mandatory)
    if (!uploadedDocument) {
      newErrors.document = 'Bitte laden Sie Ihren Fahrzeugschein hoch';
      isValid = false;
    }

    // Validate service voucher selection
    if (serviceVoucher === null) {
      newErrors.voucher = 'Bitte wählen Sie eine Option';
      isValid = false;
    }
    
    // Validate annual renewal selection
    if (wantsAnnualRenewal === null) {
      newErrors.annualRenewal = 'Bitte wählen Sie eine Option';
      isValid = false;
    }
    
    // Validate agreements
    if (!agreements?.thgNotice) {
      newErrors.thgNotice = 'Bestätigung erforderlich';
      isValid = false;
    }
    if (!agreements?.authorization) {
      newErrors.authorization = 'Bestätigung erforderlich';
      isValid = false;
    }
    if (!agreements?.privacy) {
      newErrors.privacy = 'Bestätigung erforderlich';
      isValid = false;
    }
    if (!agreements?.terms) {
      newErrors.terms = 'Bestätigung erforderlich';
      isValid = false;
    }
    if (!agreements?.voucherUnderstanding) {
      newErrors.voucherUnderstanding = 'Bestätigung erforderlich';
      isValid = false;
    }

    setErrors(newErrors);
    setTouched(Object.keys(registrationValidationSchema)?.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}));
    
    // Log validation errors for debugging
    if (!isValid) {
      console.log('Form validation failed:', newErrors);
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      // Show error message with specific issues
      const errorMessages = Object.values(errors)?.filter(Boolean);
      setError(`Bitte korrigieren Sie folgende Fehler: ${errorMessages?.join(', ')}`);
      
      // Scroll to first error
      const firstError = document.querySelector('[aria-invalid="true"]');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // Check for duplicate license plate BEFORE submission
    if (!proceedWithDuplicate && formData?.licensePlate) {
      try {
        const { data: existingApps, error: checkError } = await supabase
          ?.from('applications')
          ?.select('customer_name, customer_email, application_status, application_date')
          ?.eq('customer_license_plate', formData?.licensePlate?.trim()?.toUpperCase());

        if (checkError) {
          console.error('Error checking for duplicate license plate:', checkError);
          // Continue with submission even if check fails
        } else if (existingApps && existingApps?.length > 0) {
          // Found duplicate license plate - show warning modal
          setDuplicateApplications(existingApps);
          setShowDuplicateWarning(true);
          return; // Stop submission and wait for user decision
        }
      } catch (err) {
        console.error('Error during duplicate check:', err);
        // Continue with submission even if check fails
      }
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      let vehicleDocumentUrl = null;

      // Upload vehicle document to Supabase Storage if provided
      if (uploadedDocument?.file) {
        const fileName = `${uploadedDocument?.chassisNumber || 'unknown'}_${Date.now()}.${uploadedDocument?.file?.name?.split('.')?.pop()}`;
        const filePath = `${dealershipId}/${fileName}`;

        const { data: uploadData, error: uploadError } = await supabase?.storage?.from('vehicle-documents')?.upload(filePath, uploadedDocument?.file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading vehicle document:', uploadError);
          throw new Error(`Fehler beim Hochladen des Fahrzeugscheins: ${uploadError?.message || 'Bitte versuchen Sie es erneut.'}`);
        }

        // Get public URL for the uploaded document (bucket is now public)
        const { data: urlData } = supabase?.storage?.from('vehicle-documents')?.getPublicUrl(filePath);

        vehicleDocumentUrl = urlData?.publicUrl;
      }

      // Create application record with customer data
      const confirmationToken = `${Date.now()}_${Math.random()?.toString(36)?.substring(2, 15)}`;
      
      const { data: newApplication, error: insertError } = await supabase
        ?.from('applications')
        ?.insert([
          {
            customer_name: `${formData?.firstname} ${formData?.lastname}`,
            customer_email: formData?.email,
            customer_phone: formData?.phone || null,
            customer_license_plate: formData?.licensePlate || null,
            customer_gender: formData?.gender,
            company_name: formData?.gender === 'company' ? formData?.companyName : null,
            chassis_number: uploadedDocument?.chassisNumber || null,
            vehicle_model: null,
            vehicle_document_url: vehicleDocumentUrl,
            dealership_id: dealershipId || null,
            application_status: 'unbestätigt',
            application_date: new Date()?.toISOString(),
            wants_annual_renewal: wantsAnnualRenewal,
            confirmation_token: confirmationToken,
            confirmation_sent_at: new Date()?.toISOString(),
            notes: serviceVoucher ? 'Customer opted in for automatic next year enrollment and service voucher' : null
          }
        ])
        ?.select()
        ?.single();

      if (insertError) throw insertError;

      // AI extraction removed - documents are now uploaded without automatic data extraction
      // This ensures reliable document upload and storage without dependency on external AI services
      console.log('✅ Application created successfully with document URL:', vehicleDocumentUrl);

      // Get dealership name for email
      let dealershipName = 'Ihr Autohaus';
      if (dealershipId) {
        const { data: dealershipData, error: dealershipError } = await supabase
          ?.from('dealerships')
          ?.select('name, status')
          ?.eq('id', dealershipId)
          ?.single();

        if (dealershipError) {
          console.error('Error fetching dealership:', dealershipError);
          // Continue with default name
        } else if (dealershipData) {
          if (dealershipData?.status === 'active') {
            dealershipName = dealershipData?.name;
          } else {
            console.warn('Dealership is not active:', dealershipData);
            dealershipName = dealershipData?.name; // Still use the name even if inactive
          }
        } else {
          console.warn('Dealership not found for ID:', dealershipId);
        }
      } else {
        console.warn('No dealership ID provided during registration');
      }

      // Send registration confirmation email automatically
      try {
        await sendRegistrationConfirmation({
          customerName: `${formData?.firstname} ${formData?.lastname}`,
          customerEmail: formData?.email,
          customerLicensePlate: formData?.licensePlate || 'Nicht angegeben',
          dealershipName: dealershipName,
          confirmationToken: confirmationToken,
          applicationDate: new Date()?.toISOString(),
          applicationId: newApplication?.id,
          dealershipId: dealershipId
        });
      } catch (emailError) {
        console.error('Error sending registration confirmation email:', emailError);
        // Don't throw error - registration was successful, just email failed
      }

      // Send admin notification for new application - ONLY if status is not 'unbestätigt'
      // Admin should only be notified after customer confirms their email
      // This will be handled by the confirmation page

      if (onSubmit) {
        onSubmit(newApplication);
      }

      // Show "Register another vehicle" button after successful submission
      setShowRegisterAnotherButton(true);

      // Reset form
      setFormData({
        firstname: '',
        lastname: '',
        gender: '',
        companyName: '',
        street: '',
        postalCode: '',
        city: '',
        birthdate: '',
        email: '',
        phone: '',
        licensePlate: ''
      });
      setUploadedDocument(null);
      setServiceVoucher(null);
      setWantsAnnualRenewal(null);
      setAgreements({
        thgNotice: false,
        authorization: false,
        privacy: false,
        terms: false,
        voucherUnderstanding: false
      });
      setTouched({});
      setErrors({});
      setProceedWithDuplicate(false); // Reset duplicate flag
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err?.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDocumentUpload = (uploadData) => {
    setUploadedDocument(uploadData);
    setShowUploadModal(false);
    if (errors?.document) {
      setErrors(prev => ({ ...prev, document: '' }));
    }
    // Notify parent component
    if (onDocumentUpload) {
      onDocumentUpload(uploadData);
    }
  };

  const handleRegisterAnotherVehicle = () => {
    // Prefill form with existing data except license plate and voucher code
    // Keep all personal data but clear vehicle-specific fields
    setUploadedDocument(null);
    setServiceVoucher(null);
    setWantsAnnualRenewal(null);
    setFormData(prev => ({
      ...prev,
      licensePlate: ''
    }));
    // Reset privacy checkboxes for data protection compliance
    setAgreements({
      thgNotice: false,
      authorization: false,
      privacy: false,
      terms: false,
      voucherUnderstanding: false
    });
    setTouched({});
    setErrors({});
    setError('');
    setShowRegisterAnotherButton(false);
    setProceedWithDuplicate(false); // Reset duplicate flag
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateWarning(false);
    setDuplicateApplications([]);
    setProceedWithDuplicate(false);
  };

  const handleDuplicateProceed = () => {
    setShowDuplicateWarning(false);
    setProceedWithDuplicate(true);
    // Trigger form submission again with duplicate flag set
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) {
        form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }, 100);
  };

  return (
    <>
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-lg shadow-card p-6 md:p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                340 {voucherCurrency} Gutschein jetzt reservieren
              </h2>
              <p className="text-muted-foreground">
                Füllen Sie das Formular aus und laden Sie Ihren Fahrzeugschein hoch. Nach erfolgreicher Prüfung erhalten Sie Ihren Gutschein – einlösbar bei {dealership?.name || 'Ihrem Partner'}.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Antragsteller <span className="text-error">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button
                    type="button"
                    variant={formData?.gender === 'male' ? 'default' : 'outline'}
                    fullWidth
                    iconName="User"
                    iconPosition="left"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, gender: 'male' }));
                      if (errors?.gender) setErrors(prev => ({ ...prev, gender: '' }));
                    }}
                  >
                    Herr
                  </Button>
                  <Button
                    type="button"
                    variant={formData?.gender === 'female' ? 'default' : 'outline'}
                    fullWidth
                    iconName="User"
                    iconPosition="left"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, gender: 'female' }));
                      if (errors?.gender) setErrors(prev => ({ ...prev, gender: '' }));
                    }}
                  >
                    Frau
                  </Button>
                  <Button
                    type="button"
                    variant={formData?.gender === 'other' ? 'default' : 'outline'}
                    fullWidth
                    iconName="User"
                    iconPosition="left"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, gender: 'other' }));
                      if (errors?.gender) setErrors(prev => ({ ...prev, gender: '' }));
                    }}
                  >
                    Divers
                  </Button>
                  <Button
                    type="button"
                    variant={formData?.gender === 'company' ? 'default' : 'outline'}
                    fullWidth
                    iconName="Building2"
                    iconPosition="left"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, gender: 'company' }));
                      if (errors?.gender) setErrors(prev => ({ ...prev, gender: '' }));
                    }}
                  >
                    Firma
                  </Button>
                </div>
                {errors?.gender && (
                  <p className="text-sm text-error mt-2 flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} />
                    {errors?.gender}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label={formData?.gender === 'company' ? 'Ansprechpartner Vorname' : 'Vorname'}
                  type="text"
                  name="firstname"
                  placeholder="Max"
                  value={formData?.firstname}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('firstname')}
                  error={touched?.firstname ? errors?.firstname : ''}
                  required
                  aria-invalid={!!errors?.firstname}
                />
                
                <Input
                  label={formData?.gender === 'company' ? 'Ansprechpartner Nachname' : 'Nachname'}
                  type="text"
                  name="lastname"
                  placeholder="Mustermann"
                  value={formData?.lastname}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('lastname')}
                  error={touched?.lastname ? errors?.lastname : ''}
                  required
                  aria-invalid={!!errors?.lastname}
                />
              </div>

              {formData?.gender === 'company' && (
                <Input
                  label="Firmenname"
                  type="text"
                  name="companyName"
                  placeholder="Musterfirma GmbH"
                  value={formData?.companyName}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('companyName')}
                  error={touched?.companyName ? errors?.companyName : ''}
                  required
                  aria-invalid={!!errors?.companyName}
                />
              )}

              <Input
                label="Straße und Hausnummer"
                type="text"
                name="street"
                placeholder="Musterstraße 123"
                value={formData?.street}
                onChange={handleInputChange}
                onBlur={() => handleBlur('street')}
                error={touched?.street ? errors?.street : ''}
                required
                aria-invalid={!!errors?.street}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Postleitzahl"
                  type="text"
                  name="postalCode"
                  placeholder="12345"
                  value={formData?.postalCode}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('postalCode')}
                  error={touched?.postalCode ? errors?.postalCode : ''}
                  maxLength={5}
                  required
                  aria-invalid={!!errors?.postalCode}
                />
                
                <div className="md:col-span-2">
                  <Input
                    label="Stadt"
                    type="text"
                    name="city"
                    placeholder="München"
                    value={formData?.city}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('city')}
                    error={touched?.city ? errors?.city : ''}
                    required
                    aria-invalid={!!errors?.city}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formData?.gender !== 'company' && (
                  <Input
                    label="Geburtsdatum"
                    type="date"
                    name="birthdate"
                    value={formData?.birthdate}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('birthdate')}
                    error={touched?.birthdate ? errors?.birthdate : ''}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 16))?.toISOString()?.split('T')?.[0]}
                    required
                    aria-invalid={!!errors?.birthdate}
                  />
                )}
                
                <Input
                  label="E-Mail-Adresse"
                  type="email"
                  name="email"
                  placeholder="max.mustermann@example.com"
                  value={formData?.email}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('email')}
                  error={touched?.email ? errors?.email : ''}
                  required
                  aria-invalid={!!errors?.email}
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <Input
                  label="Kennzeichen"
                  name="licensePlate"
                  type="text"
                  placeholder="HH-XY 5678"
                  value={formData?.licensePlate}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('licensePlate')}
                  error={touched?.licensePlate ? errors?.licensePlate : ''}
                  iconName="Car"
                  required
                  aria-invalid={!!errors?.licensePlate}
                />
              </div>

              <Input
                label="Telefonnummer (optional)"
                type="tel"
                name="phone"
                placeholder="+49 152 123 456 78"
                value={formData?.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched?.phone && errors?.phone}
                iconName="Phone"
              />

              <div className="border-t border-border pt-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Fahrzeugschein hochladen
                </h3>
                
                {!uploadedDocument ? (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="outline"
                      fullWidth
                      size="lg"
                      iconName="Upload"
                      iconPosition="left"
                      onClick={() => setShowUploadModal(true)}
                    >
                      Fahrzeugschein hochladen
                    </Button>
                    {errors?.document && (
                      <p className="text-sm text-error flex items-center gap-2">
                        <Icon name="AlertCircle" size={16} />
                        {errors?.document}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-success/20 via-success/10 to-success/5 border-4 border-success rounded-xl p-6 shadow-lg animate-fade-in">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center shadow-lg animate-pulse">
                            <Icon name="CheckCircle2" size={36} color="white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <p className="font-bold text-foreground text-xl">✓ Fahrzeugschein erfolgreich hochgeladen</p>
                            <div className="px-3 py-1 bg-success rounded-full shadow-sm">
                              <span className="text-sm font-bold text-white">BESTÄTIGT</span>
                            </div>
                          </div>
                          <div className="bg-white/80 rounded-lg p-4 space-y-3 border-2 border-success/30">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center">
                                <Icon name="FileText" size={18} color="var(--color-success)" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-muted-foreground font-medium">Dokument</p>
                                <p className="text-sm font-bold text-foreground">{uploadedDocument?.file?.name}</p>
                              </div>
                            </div>
                            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <Icon name="Info" size={16} color="var(--color-blue-500)" className="flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-muted-foreground">
                                  Die Fahrgestellnummer (FIN) wird vom Administrator bei der Prüfung erfasst.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 p-4 bg-success rounded-lg shadow-sm">
                            <div className="flex items-start gap-3">
                              <Icon name="ShieldCheck" size={20} color="white" className="flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-bold text-white mb-1">Ihre Daten wurden erfolgreich gespeichert</p>
                                <p className="text-xs text-white/90">
                                  Der Fahrzeugschein wird bei der Antragstellung automatisch übermittelt.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => setUploadedDocument(null)}
                        className="flex-shrink-0"
                      >
                        Entfernen
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-6">
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-2">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="TrendingUp" size={22} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                        {has2027
                          ? `Jetzt auch 2027 sichern – bis zu ${totalPremium} ${voucherCurrency} Gesamtvorteil`
                          : `${premium2026} ${voucherCurrency} Gutschein für 2026 sichern`
                        }
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {has2027
                          ? `Registrieren Sie Ihre THG-Quote für 2026 und 2027 gleichzeitig und sichern Sie sich den maximalen Gutschein bei ${dealership?.name || 'Ihrem Partner'}. Kein erneuter Aufwand im nächsten Jahr.`
                          : `Registrieren Sie Ihre THG-Quote für 2026 und sichern Sie sich ${premium2026} ${voucherCurrency} Gutschein bei ${dealership?.name || 'Ihrem Partner'}.`
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant={serviceVoucher === true ? 'default' : 'outline'}
                      fullWidth
                      iconName="Check"
                      iconPosition="left"
                      onClick={() => {
                        setServiceVoucher(true);
                        setWantsAnnualRenewal(true);
                        if (errors?.voucher) setErrors(prev => ({ ...prev, voucher: '' }));
                        if (errors?.annualRenewal) setErrors(prev => ({ ...prev, annualRenewal: '' }));
                      }}
                    >
                      {has2027 ? `Ja, ${totalPremium} ${voucherCurrency} Vorteil sichern` : `Ja, ${premium2026} ${voucherCurrency} Gutschein sichern`}
                    </Button>
                    <Button
                      type="button"
                      variant={serviceVoucher === false ? 'default' : 'outline'}
                      fullWidth
                      iconName="Minus"
                      iconPosition="left"
                      onClick={() => {
                        setServiceVoucher(false);
                        setWantsAnnualRenewal(false);
                        if (errors?.voucher) setErrors(prev => ({ ...prev, voucher: '' }));
                        if (errors?.annualRenewal) setErrors(prev => ({ ...prev, annualRenewal: '' }));
                      }}
                    >
                      Nein, nur dieses Jahr
                    </Button>
                  </div>
                  {(errors?.voucher || errors?.annualRenewal) && (
                    <p className="text-sm text-error mt-2 flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} />
                      {errors?.voucher || errors?.annualRenewal}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t border-border pt-6 space-y-4">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Bestätigungen
                </h3>
                
                <div className="space-y-3">
                  <Checkbox
                    label={
                      <span className="text-sm">
                        Ich trete das mir zustehende CO₂‑Zertifikat (THG‑Quote) für mein Elektrofahrzeug an die Grid Balance UG ab.
                      </span>
                    }
                    checked={agreements?.thgNotice}
                    onChange={(e) => {
                      setAgreements(prev => ({ ...prev, thgNotice: e?.target?.checked }));
                      if (errors?.thgNotice) setErrors(prev => ({ ...prev, thgNotice: '' }));
                    }}
                    error={errors?.thgNotice}
                  />
                  
                  <Checkbox
                    label={
                      <span className="text-sm">
                        Ich bevollmächtige die Grid Balance UG, die komplette Abwicklung für mich zu übernehmen.
                      </span>
                    }
                    checked={agreements?.authorization}
                    onChange={(e) => {
                      setAgreements(prev => ({ ...prev, authorization: e?.target?.checked }));
                      if (errors?.authorization) setErrors(prev => ({ ...prev, authorization: '' }));
                    }}
                    error={errors?.authorization}
                  />
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="privacy"
                      checked={agreements?.privacy}
                      onChange={(e) => setAgreements(prev => ({ ...prev, privacy: e?.target?.checked }))}
                      aria-invalid={!!errors?.privacy}
                    />
                    <div className="flex-1 flex items-center gap-2">
                      <label htmlFor="privacy" className="text-sm text-muted-foreground cursor-pointer">
                        Ich habe die Datenschutzerklärung gelesen und bin einverstanden.
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-primary hover:text-primary/80 transition-smooth text-sm font-medium underline"
                      >
                        Datenschutzerklärung aufrufen
                      </button>
                    </div>
                  </div>
                  {errors?.privacy && <p className="text-sm text-destructive ml-8">{errors?.privacy}</p>}
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms"
                      checked={agreements?.terms}
                      onChange={(e) => setAgreements(prev => ({ ...prev, terms: e?.target?.checked }))}
                      aria-invalid={!!errors?.terms}
                    />
                    <div className="flex-1 flex items-center gap-2">
                      <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                        Ich habe die AGB gelesen und akzeptiere sie.
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-primary hover:text-primary/80 transition-smooth text-sm font-medium underline"
                      >
                        AGB aufrufen
                      </button>
                    </div>
                  </div>
                  {errors?.terms && <p className="text-sm text-destructive ml-8">{errors?.terms}</p>}
                  
                  <Checkbox
                    label={
                      <span className="text-sm">
                        Ich habe verstanden, dass ich als Gegenleistung einen {voucherDisplay} Wertgutschein, den ich beim Partner einlösen kann, erhalte.
                      </span>
                    }
                    checked={agreements?.voucherUnderstanding}
                    onChange={(e) => {
                      setAgreements(prev => ({ ...prev, voucherUnderstanding: e?.target?.checked }));
                      if (errors?.voucherUnderstanding) setErrors(prev => ({ ...prev, voucherUnderstanding: '' }));
                    }}
                    error={errors?.voucherUnderstanding}
                  />
                </div>
              </div>

              <div className="pt-6">
                {Object.keys(errors)?.length > 0 && (
                  <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-error mb-2">Bitte korrigieren Sie folgende Fehler:</p>
                        <ul className="text-sm text-error space-y-1 list-disc list-inside">
                          {errors?.firstname && <li>Vorname: {errors?.firstname}</li>}
                          {errors?.lastname && <li>Nachname: {errors?.lastname}</li>}
                          {errors?.gender && <li>Antragsteller: {errors?.gender}</li>}
                          {errors?.companyName && <li>Firmenname: {errors?.companyName}</li>}
                          {errors?.street && <li>Straße und Hausnummer: {errors?.street}</li>}
                          {errors?.postalCode && <li>Postleitzahl: {errors?.postalCode}</li>}
                          {errors?.city && <li>Stadt: {errors?.city}</li>}
                          {formData?.gender !== 'company' && errors?.birthdate && <li>Geburtsdatum: {errors?.birthdate}</li>}
                          {errors?.email && <li>E-Mail: {errors?.email}</li>}
                          {errors?.phone && <li>Telefonnummer: {errors?.phone}</li>}
                          {errors?.licensePlate && <li>Kennzeichen: {errors?.licensePlate}</li>}
                          {errors?.document && <li>Fahrzeugschein: {errors?.document}</li>}
                          {(errors?.voucher || errors?.annualRenewal) && <li>2027-Option: {errors?.voucher || errors?.annualRenewal}</li>}
                          {errors?.thgNotice && <li>THG-Quote Abtretung: {errors?.thgNotice}</li>}
                          {errors?.authorization && <li>Bevollmächtigung: {errors?.authorization}</li>}
                          {errors?.privacy && <li>Datenschutzerklärung: {errors?.privacy}</li>}
                          {errors?.terms && <li>AGB: {errors?.terms}</li>}
                          {errors?.voucherUnderstanding && <li>Gutschein-Verständnis: {errors?.voucherUnderstanding}</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  size="lg"
                  loading={isSubmitting}
                  iconName="Gift"
                  iconPosition="left"
                >
                  340 {voucherCurrency} Gutschein jetzt sichern
                </Button>
                {showRegisterAnotherButton && (
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    size="lg"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleRegisterAnotherVehicle}
                    className="mt-4"
                  >
                    Weiteres Fahrzeug registrieren
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleDocumentUpload}
      />
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
      <AGBModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      {showDuplicateWarning && (
        <DuplicateLicensePlateModal
          licensePlate={formData?.licensePlate}
          existingApplications={duplicateApplications}
          onCancel={handleDuplicateCancel}
          onProceed={handleDuplicateProceed}
        />
      )}
      {showThgModal && (
        <LegalModal
          title="Wichtige Hinweise zur THG-Quote"
          content={`Die Treibhausgasminderungsquote (THG-Quote) ist ein gesetzliches Instrument zur Förderung der Elektromobilität in Deutschland.\n\nDurch die Anmeldung Ihres Elektrofahrzeugs können Sie jährlich von dieser Prämie profitieren.\n\nWichtige Punkte:\n• Die Prämie wird einmal jährlich ausgezahlt\n• Ihr Fahrzeug muss auf Sie zugelassen sein\n• Die Fahrgestellnummer wird zur Vermeidung von Doppelregistrierungen geprüft\n• Die Auszahlung erfolgt nach erfolgreicher Prüfung durch das Umweltbundesamt\n\nBei Fragen wenden Sie sich bitte an Ihren Partner.`}
          onClose={() => setShowThgModal(false)}
          onConfirm={() => {
            setAgreements(prev => ({ ...prev, thgNotice: true }));
            setShowThgModal(false);
            if (errors?.thgNotice) setErrors(prev => ({ ...prev, thgNotice: '' }));
          }}
        />
      )}
    </>
  );
};

export default RegistrationForm;